import { useState, useEffect } from 'react';
import apiClient from '../../clients/apiClient';
import Spinner from './Spinner';

interface ResumePreviewModalProps {
  open: boolean;
  onClose: () => void;
}

// Professional SVG Eye Icon
function EyeIcon({ className = '', style }: { className?: string, style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

export { EyeIcon };

/**
 * A modal component that fetches and securely displays the user's resume PDF.
 *
 * Security: The Supabase signed URL returned by the backend is consumed only
 * once to fetch the raw PDF bytes. Those bytes are then converted into an
 * opaque `blob://` URL which is used for both the <iframe> src and the
 * "Open in new tab" link. The browser address bar never exposes the
 * underlying Supabase storage URL, bucket name, or file path.
 *
 * @param open    - Whether the modal is currently visible
 * @param onClose - Callback to close the modal
 */
export default function ResumePreviewModal({ open, onClose }: ResumePreviewModalProps) {

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading]  = useState(false);
  const [error, setError]      = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Fetch signed URL → download PDF bytes → create opaque blob:// URL
  useEffect(() => {
    if (!open) return;

    setError(null);
    setBlobUrl(null);
    setLoading(true);

    // Local refs so the cleanup closure can revoke the right URL and abort stale updates
    let objectUrl: string | null = null;
    let cancelled = false;

    apiClient
      .get<{ url: string; filename: string }>('/api/user/resume-pdf')
      .then(({ data }) => fetch(data.url))
      .then(res => {
        if (!res.ok) throw new Error(`PDF fetch failed (${res.status})`);
        return res.blob();
      })
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load resume preview. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Revoke the object URL when the modal closes or the effect re-runs (retry)
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, retryCount]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(28,25,23,0.5)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-card shadow-warm-lg w-full flex flex-col animate-slide-up overflow-hidden"
        style={{ maxWidth: '800px', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded flex items-center justify-center" style={{ backgroundColor: 'var(--sand)' }}>
              <EyeIcon className="h-4 w-4" style={{ color: 'var(--ink2)' } as React.CSSProperties} />
            </div>
            <div>
              <p className="text-sm font-semibold text-ink">Resume Preview</p>
              <p className="text-xs text-muted">PDF viewer — read only</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold text-g hover:text-emerald-700 border border-g-border bg-g-light rounded-input px-3 py-1.5 transition-colors"
                title="Open in new tab"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open ↗
              </a>
            )}
            <button
              onClick={onClose}
              className="h-7 w-7 flex items-center justify-center rounded text-muted hover:text-ink hover:bg-bg transition-colors text-lg leading-none"
              aria-label="Close preview"
            >
              ×
            </button>
          </div>
        </div>

        {/* Mobile helper notice */}
        {blobUrl && !loading && !error && (
          <div className="sm:hidden px-4 py-2 bg-g-light border-b border-g-border flex items-center justify-between text-xs">
            <span className="text-g-text font-medium">Viewing on mobile?</span>
            <a
              href={blobUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-g underline ml-2"
            >
              Open PDF in full screen ↗
            </a>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-hidden" style={{ minHeight: '60vh' }}>
          {loading && (
            <div className="flex items-center justify-center h-full py-20">
              <div className="flex flex-col items-center gap-3">
                <Spinner />
                <p className="text-xs text-muted">Loading resume…</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-full py-20 px-6 text-center">
              <svg className="h-8 w-8 text-hint mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium text-ink mb-1">Preview unavailable</p>
              <p className="text-xs text-muted mb-4">{error}</p>
              <button
                onClick={() => setRetryCount(c => c + 1)}
                className="text-xs font-medium px-4 py-1.5 rounded-input border border-border hover:bg-sand transition-colors text-ink"
              >
                Try again
              </button>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <iframe
              src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              className="w-full h-full border-0"
              style={{ minHeight: '60vh' }}
              title="Resume PDF Preview"
            />
          )}
        </div>
      </div>
    </div>
  );
}
